import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <header className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
      </header>
      
      <main className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
        <p className="text-slate-500 mb-8">Last updated: February 2026</p>
        
        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-4">
              By accessing or using Savecation ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 mb-4">
              Savecation is a travel savings platform that helps users save money for their travel goals. 
              The Service includes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Personal and group savings goals management</li>
              <li>AI-powered travel planning and recommendations</li>
              <li>Deal discovery for flights, hotels, and activities</li>
              <li>Wallet and payment processing via Stripe</li>
              <li>Community features for travel enthusiasts</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">3. User Accounts</h2>
            <p className="text-slate-600 mb-4">
              To use certain features of the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">4. Payments and Subscriptions</h2>
            <p className="text-slate-600 mb-4">
              Savecation offers free and paid subscription tiers. By subscribing to a paid plan:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>You authorize us to charge your payment method on a recurring basis</li>
              <li>Subscription fees are non-refundable except as required by law</li>
              <li>You may cancel your subscription at any time</li>
              <li>Price changes will be communicated in advance</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Wallet funds are used for reservations and in-app purchases. Unused wallet funds may be 
              refundable upon account closure, subject to our refund policy.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">5. User Conduct</h2>
            <p className="text-slate-600 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post false, misleading, or fraudulent content</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with the proper functioning of the Service</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">6. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              All content, features, and functionality of the Service are owned by Savecation and are 
              protected by copyright, trademark, and other intellectual property laws. You may not 
              reproduce, distribute, or create derivative works without our express permission.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">7. Third-Party Services</h2>
            <p className="text-slate-600 mb-4">
              The Service integrates with third-party providers for flights, hotels, and activities. 
              We do not guarantee the accuracy of third-party information and are not responsible for 
              third-party services or transactions.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              To the maximum extent permitted by law, Savecation shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use of the Service.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">9. Termination</h2>
            <p className="text-slate-600 mb-4">
              We may terminate or suspend your account at any time for violation of these terms. 
              You may delete your account at any time through the Settings page.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">10. Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We may update these Terms from time to time. We will notify you of significant changes 
              via email or in-app notification. Continued use of the Service constitutes acceptance 
              of the updated Terms.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">11. Contact Us</h2>
            <p className="text-slate-600">
              If you have questions about these Terms, please contact us at:
              <br />
              <a href="mailto:support@savecation.app" className="text-sky-600 hover:underline">
                support@savecation.app
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
